import React, { useEffect, useRef } from 'react';

export type ColumnKey = 'number' | 'customer' | 'issued' | 'due' | 'amount' | 'status';

type Column = { key: ColumnKey; label: string };

export const COLUMNS: Column[] = [
  { key: 'number',   label: 'Invoice number' },
  { key: 'customer', label: 'Customer' },
  { key: 'issued',   label: 'Issued' },
  { key: 'due',      label: 'Due' },
  { key: 'amount',   label: 'Amount' },
  { key: 'status',   label: 'Status' },
];

export const DEFAULT_VISIBLE: Record<ColumnKey, boolean> = {
  number: true, customer: true, issued: true, due: true, amount: true, status: true,
};

const PANEL: React.CSSProperties = {
  position: 'absolute',
  top: 42,
  right: 0,
  width: 220,
  background: '#FFFFFF',
  border: '1px solid #E7EAF0',
  borderRadius: 10,
  boxShadow: '0 10px 30px -8px rgba(14,26,26,.18)',
  padding: 10,
  zIndex: 20,
};

const HEAD: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: '#66748B',
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  padding: '4px 8px 8px',
};

const ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 8px',
  fontSize: 13,
  cursor: 'pointer',
  borderRadius: 6,
};

const CB: React.CSSProperties = { width: 16, height: 16, accentColor: '#15A08C' };

const FOOT: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 8px 0',
  borderTop: '1px solid #F0F2F5',
  marginTop: 6,
};

const RESET: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  fontSize: 12,
  color: '#0F6E56',
  cursor: 'pointer',
  fontWeight: 600,
  padding: '4px 0',
};

type Props = {
  visible: Record<ColumnKey, boolean>;
  onChange: (next: Record<ColumnKey, boolean>) => void;
  onClose: () => void;
};

export default function ColumnsPicker({ visible, onChange, onClose }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const toggle = (k: ColumnKey) => {
    onChange({ ...visible, [k]: !visible[k] });
  };

  return (
    <div ref={ref} style={PANEL} role="menu">
      <div style={HEAD}>Show columns</div>
      {COLUMNS.map((c) => (
        <label
          key={c.key}
          style={ROW}
          onMouseOver={(e) => (e.currentTarget.style.background = '#F7F8FA')}
          onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <input
            type="checkbox"
            style={CB}
            checked={visible[c.key]}
            onChange={() => toggle(c.key)}
          />
          <span>{c.label}</span>
        </label>
      ))}
      <div style={FOOT}>
        <button type="button" style={RESET} onClick={() => onChange({ ...DEFAULT_VISIBLE })}>
          Reset to default
        </button>
      </div>
    </div>
  );
}